import { Fragment, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import './index.scss';
import NavSearchBar from '@/components/SearchBar/NavSearchBar';
import PdfViewerModal, { shouldOpenPdfInNewTab } from '@/components/PdfViewerModal';
import useBodyScrollLock from '@/hooks/useBodyScrollLock';
import { academyMenu } from './academyMenu';
import img_components_sitetopnav_default_logo_src from '@/assets/images/components-sitetopnav-default-logo-src.png';
import img_components_sitetopnav_light_logo_src from '@/assets/images/components-sitetopnav-light-logo-src.png';

const DEFAULT_LOGO_SRC = img_components_sitetopnav_default_logo_src;

const AcademyLinkLabel = ({ link }) => (
  <span className="site-topnav__academy-label">
    {link.icon ? (
      <span
        className="site-topnav__academy-icon"
        style={{ '--academy-icon': `url("${link.icon}")` }}
        aria-hidden="true"
      />
    ) : null}
    <span>{link.label}</span>
  </span>
);

/** Colored wordmark + icon (light header) */
const LIGHT_LOGO_SRC = img_components_sitetopnav_light_logo_src;

const HamburgerIcon = props => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false" {...props}>
    <path
      d="M4 7h16M4 12h16M4 17h16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const CloseIcon = props => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false" {...props}>
    <path
      d="M6 6l12 12M18 6L6 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const ChevronIcon = props => (
  <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true" focusable="false" {...props}>
    <path
      d="M6 9l6 6 6-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const isNavigable = link => Boolean(link.href || link.pdf);

/** Flattened, stable list (DOM order) of navigable links across every group, including nested children */
const flattenNavigableItems = groups =>
  groups.flatMap(group =>
    group.items.flatMap(i => [
      ...(isNavigable(i) ? [i] : []),
      ...(i.children ?? []).filter(isNavigable),
    ])
  );

/**
 * PDF items are real links (so modifier-clicks and mobile open a new tab), but a
 * plain click on desktop opens the in-page viewer instead.
 */
const PdfLink = ({ link, className, onBeforeOpen, onOpenPdf, ...rest }) => (
  <a
    {...rest}
    className={className}
    href={link.pdf.url}
    target="_blank"
    rel="noopener noreferrer"
    onClick={e => {
      onBeforeOpen?.();
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || shouldOpenPdfInNewTab()) return;
      e.preventDefault();
      onOpenPdf?.(link.pdf);
    }}
  >
    <AcademyLinkLabel link={link} />
  </a>
);

const FeaturedArticleCard = ({ item, onNavigate, indexOf, focusItemAt, onEscape }) => (
  <div className="site-topnav__mega-featured">
    {item.image ? (
      <img
        className="site-topnav__mega-featured-image"
        alt=""
        referrerPolicy="no-referrer"
        src={item.image}
      />
    ) : null}
    <h3 className="site-topnav__mega-featured-title">{item.label}</h3>
    {item.description ? (
      <p className="site-topnav__mega-featured-desc">{item.description}</p>
    ) : null}
    <NavLink
      role="menuitem"
      tabIndex={-1}
      className="site-topnav__mega-featured-cta"
      to={item.href}
      onClick={onNavigate}
      onKeyDown={e => {
        const idx = indexOf(item);
        if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
          e.preventDefault();
          focusItemAt(idx + 1);
        }
        if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
          e.preventDefault();
          focusItemAt(idx - 1);
        }
        if (e.key === 'Escape') onEscape();
      }}
    >
      Read Article <span aria-hidden="true">→</span>
    </NavLink>
  </div>
);

/** Premium hover/click/focus/keyboard full-width mega menu (desktop) for Academy */
const AcademyMegaMenu = ({ item, onOpenPdf }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [panelTop, setPanelTop] = useState(0);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const closeTimerRef = useRef(null);
  const wasOpenBeforeClickRef = useRef(false);
  const suppressFocusOpenRef = useRef(false);
  const menuId = useId();

  const navigableItems = useMemo(() => flattenNavigableItems(item.groups), [item.groups]);
  const indexOf = link => navigableItems.indexOf(link);

  const isChildActive = navigableItems.some(
    link =>
      link.href &&
      (location.pathname === link.href || location.pathname.startsWith(`${link.href}/`))
  );

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setOpen(false), 160);
  };

  // Measure + open in the same handler (batched into one render) so the panel's
  // very first paint already has the correct `top` — never a frame at top:0
  // covering the trigger, which would otherwise fire a spurious mouseleave and
  // auto-close the menu immediately after it opens.
  const measureTop = () => {
    const navEl = triggerRef.current?.closest('.site-topnav');
    return navEl ? navEl.getBoundingClientRect().bottom : 0;
  };

  const openMenu = () => {
    setPanelTop(measureTop());
    setOpen(true);
  };

  const handleRegionEnter = () => {
    clearCloseTimer();
    openMenu();
  };

  const handleRegionLeave = () => scheduleClose();

  const handleBlur = e => {
    const next = e.relatedTarget;
    if (containerRef.current?.contains(next) || panelRef.current?.contains(next)) return;
    setOpen(false);
  };

  // Closing on Escape moves focus back to the trigger for a11y — but a native
  // .focus() call synchronously fires the trigger's onFocus, which would
  // otherwise reopen the menu we just closed. Suppress that one reopen.
  const closeAndRefocus = () => {
    suppressFocusOpenRef.current = true;
    setOpen(false);
    triggerRef.current?.focus();
  };

  const handleTriggerFocus = () => {
    if (suppressFocusOpenRef.current) {
      suppressFocusOpenRef.current = false;
      return;
    }
    openMenu();
  };

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => () => clearCloseTimer(), []);

  // Keep the panel aligned to the header while open (e.g. across resizes).
  useEffect(() => {
    if (!open) return undefined;

    const updatePosition = () => setPanelTop(measureTop());
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = e => {
      if (e.key === 'Escape') {
        closeAndRefocus();
      }
    };
    const onPointerDown = e => {
      const inTrigger = containerRef.current?.contains(e.target);
      const inPanel = panelRef.current?.contains(e.target);
      if (!inTrigger && !inPanel) setOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  const focusItemAt = index => {
    const menuItems = panelRef.current?.querySelectorAll('[role="menuitem"]');
    if (!menuItems || menuItems.length === 0) return;
    const clamped = (index + menuItems.length) % menuItems.length;
    menuItems[clamped]?.focus();
  };

  const handleLinkKeyDown = (e, link) => {
    const idx = indexOf(link);
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      focusItemAt(idx + 1);
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      if (idx <= 0) {
        triggerRef.current?.focus();
      } else {
        focusItemAt(idx - 1);
      }
    }
    if (e.key === 'Escape') closeAndRefocus();
  };

  const renderDesktopLink = (link, isChild = false) =>
    link.pdf ? (
      <PdfLink
        role="menuitem"
        tabIndex={-1}
        className={['site-topnav__mega-link', isChild ? 'site-topnav__mega-link--child' : '']
          .filter(Boolean)
          .join(' ')}
        link={link}
        onBeforeOpen={() => setOpen(false)}
        onOpenPdf={onOpenPdf}
        onKeyDown={e => handleLinkKeyDown(e, link)}
      />
    ) : link.href ? (
      <NavLink
        role="menuitem"
        tabIndex={-1}
        className={({ isActive }) =>
          [
            'site-topnav__mega-link',
            isChild ? 'site-topnav__mega-link--child' : '',
            isActive ? 'is-active' : '',
          ]
            .filter(Boolean)
            .join(' ')
        }
        to={link.href}
        onClick={() => setOpen(false)}
        onKeyDown={e => handleLinkKeyDown(e, link)}
      >
        <AcademyLinkLabel link={link} />
      </NavLink>
    ) : (
      <span
        className="site-topnav__mega-link site-topnav__mega-link--static"
        aria-disabled="true"
        tabIndex={-1}
      >
        <AcademyLinkLabel link={link} />
      </span>
    );

  return (
    <div
      className={['site-topnav__dropdown', open ? 'is-open' : ''].filter(Boolean).join(' ')}
      ref={containerRef}
      onMouseEnter={handleRegionEnter}
      onMouseLeave={handleRegionLeave}
      onBlur={handleBlur}
    >
      <button
        ref={triggerRef}
        type="button"
        className={[
          'site-topnav__link',
          'site-topnav__dropdown-trigger',
          isChildActive ? 'is-active' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={menuId}
        onMouseDown={() => {
          wasOpenBeforeClickRef.current = open;
        }}
        onClick={() => {
          if (wasOpenBeforeClickRef.current) {
            setOpen(false);
          } else {
            openMenu();
          }
        }}
        onFocus={handleTriggerFocus}
        onKeyDown={e => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            openMenu();
            requestAnimationFrame(() => focusItemAt(0));
          }
          if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
      >
        {item.label}
        <ChevronIcon className="site-topnav__dropdown-chevron" />
      </button>

      {open && typeof document !== 'undefined'
        ? createPortal(
            <div
              id={menuId}
              ref={panelRef}
              className="site-topnav__mega"
              role="menu"
              aria-label={item.label}
              style={{ top: panelTop }}
              onMouseEnter={handleRegionEnter}
              onMouseLeave={handleRegionLeave}
              onBlur={handleBlur}
            >
              <div className="site-topnav__mega-inner">
                {item.groups.map(group => {
                  const featuredItem = group.items.find(i => i.featured);
                  return (
                    <div
                      key={group.title}
                      className={[
                        'site-topnav__mega-column',
                        featuredItem ? 'site-topnav__mega-column--featured' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <p className="site-topnav__mega-heading">{group.title}</p>

                      {featuredItem ? (
                        <FeaturedArticleCard
                          item={featuredItem}
                          onNavigate={() => setOpen(false)}
                          indexOf={indexOf}
                          focusItemAt={focusItemAt}
                          onEscape={closeAndRefocus}
                        />
                      ) : (
                        <ul className="site-topnav__mega-list">
                          {group.items.map(link => (
                            <li key={link.label}>
                              {renderDesktopLink(link)}
                              {link.children ? (
                                <ul className="site-topnav__mega-sublist">
                                  {link.children.map(child => (
                                    <li key={child.label}>{renderDesktopLink(child, true)}</li>
                                  ))}
                                </ul>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

const NavShell = ({
  className,
  open,
  setOpen,
  items,
  logoSrc,
  maskId,
  onTriggerClick,
  onOpenPdf,
}) => {
  return (
    <nav
      className={[className, open ? 'is-open' : ''].filter(Boolean).join(' ')}
      aria-label="Primary"
    >
      <div className="site-topnav__bar">
        <NavLink className="site-topnav__logo" to="/" aria-label="Sakura Floors">
          <img className="site-topnav__logo-img" alt="" src={logoSrc} />
        </NavLink>

        <div className="site-topnav__links" aria-label="Desktop navigation">
          {items.map(item =>
            item.groups ? (
              <AcademyMegaMenu key={item.key} item={item} onOpenPdf={onOpenPdf} />
            ) : (
              <NavLink
                key={item.to}
                className={({ isActive }) =>
                  ['site-topnav__link', isActive ? 'is-active' : ''].filter(Boolean).join(' ')
                }
                to={item.to}
                end={item.end}
              >
                {item.label}
              </NavLink>
            )
          )}

          <NavSearchBar />
        </div>

        <div className="site-topnav__bar-search" aria-label="Search products">
          <NavSearchBar />
        </div>

        <button
          className="site-topnav__menu-btn"
          type="button"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          aria-controls={maskId}
          onClick={e => {
            onTriggerClick?.(e.currentTarget);
            setOpen(v => !v);
          }}
        >
          {open ? <CloseIcon /> : <HamburgerIcon />}
        </button>
      </div>
    </nav>
  );
};

/** Mobile: Academy renders as a top-level accordion whose panel contains one nested
 * accordion row per group (skipping groups marked `hideOnMobile`), reusing the same
 * accordion visual language as the trigger itself. */
const MobileMegaAccordion = ({ item, isOpen, onToggleTop, onNavigate, onOpenPdf }) => {
  const [openGroups, setOpenGroups] = useState(() => new Set());
  const panelId = useId();

  useEffect(() => {
    if (!isOpen) setOpenGroups(new Set());
  }, [isOpen]);

  const toggleGroup = title => {
    setOpenGroups(prev => {
      const next = new Set(prev);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }
      return next;
    });
  };

  const renderMobileLink = (link, isChild = false) =>
    link.pdf ? (
      <PdfLink
        key={link.label}
        role="menuitem"
        className={[
          'site-topnav__mobile-link',
          'site-topnav__mobile-link--subsub',
          isChild ? 'site-topnav__mobile-link--child' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        link={link}
        onBeforeOpen={onNavigate}
        onOpenPdf={onOpenPdf}
      />
    ) : link.href ? (
      <NavLink
        key={link.label}
        role="menuitem"
        className={({ isActive }) =>
          [
            'site-topnav__mobile-link',
            'site-topnav__mobile-link--subsub',
            isChild ? 'site-topnav__mobile-link--child' : '',
            isActive ? 'is-active' : '',
          ]
            .filter(Boolean)
            .join(' ')
        }
        to={link.href}
        onClick={onNavigate}
      >
        <AcademyLinkLabel link={link} />
      </NavLink>
    ) : (
      <span
        key={link.label}
        className="site-topnav__mobile-link site-topnav__mobile-link--subsub site-topnav__mobile-link--static"
        aria-disabled="true"
        tabIndex={-1}
      >
        <AcademyLinkLabel link={link} />
      </span>
    );

  return (
    <Fragment>
      <button
        type="button"
        className={[
          'site-topnav__mobile-link',
          'site-topnav__mobile-accordion-trigger',
          isOpen ? 'is-open' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onToggleTop}
      >
        {item.label}
        <ChevronIcon className="site-topnav__mobile-accordion-chevron" />
      </button>
      <div
        id={panelId}
        className={['site-topnav__mobile-submenu', isOpen ? 'is-open' : '']
          .filter(Boolean)
          .join(' ')}
      >
        <div className="site-topnav__mobile-submenu-inner">
          {item.groups
            .filter(group => !group.hideOnMobile)
            .map(group => {
              const featuredItem = group.items.find(i => i.featured);
              const groupOpen = openGroups.has(group.title);
              const groupPanelId = `${panelId}-${group.title}`;
              return (
                <Fragment key={group.title}>
                  <button
                    type="button"
                    className={[
                      'site-topnav__mobile-link',
                      'site-topnav__mobile-link--sub',
                      'site-topnav__mobile-accordion-trigger',
                      groupOpen ? 'is-open' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    aria-expanded={groupOpen}
                    aria-controls={groupPanelId}
                    onClick={() => toggleGroup(group.title)}
                  >
                    {group.title}
                    <ChevronIcon className="site-topnav__mobile-accordion-chevron" />
                  </button>
                  <div
                    id={groupPanelId}
                    className={[
                      'site-topnav__mobile-submenu',
                      'site-topnav__mobile-submenu--nested',
                      groupOpen ? 'is-open' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <div className="site-topnav__mobile-submenu-inner">
                      {featuredItem ? (
                        <div className="site-topnav__mobile-featured">
                          <p className="site-topnav__mobile-featured-title">{featuredItem.label}</p>
                          {featuredItem.description ? (
                            <p className="site-topnav__mobile-featured-desc">
                              {featuredItem.description}
                            </p>
                          ) : null}
                          <NavLink
                            role="menuitem"
                            className="site-topnav__mobile-featured-cta"
                            to={featuredItem.href}
                            onClick={onNavigate}
                          >
                            Read Article <span aria-hidden="true">→</span>
                          </NavLink>
                        </div>
                      ) : (
                        group.items.map(link => (
                          <Fragment key={link.label}>
                            {renderMobileLink(link)}
                            {link.children?.map(child => renderMobileLink(child, true))}
                          </Fragment>
                        ))
                      )}
                    </div>
                  </div>
                </Fragment>
              );
            })}
        </div>
      </div>
    </Fragment>
  );
};

const SiteTopNav = ({ className = '', logoSrc, variant = 'default' }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [showSticky, setShowSticky] = useState(false);
  const [mobileAccordionOpen, setMobileAccordionOpen] = useState(false);
  const [pdfViewer, setPdfViewer] = useState(null);
  const maskId = useId();
  const triggerRef = useRef(null);
  const shouldRefocusTriggerRef = useRef(false);

  // The trigger button is `visibility: hidden` while the menu is open (swapped
  // visually for the mask's own close button, see `.is-open .site-topnav__menu-btn`
  // below). Calling .focus() synchronously here would target that still-hidden
  // element, since React hasn't committed the class removal yet — silently a
  // no-op. Defer the actual focus() to the effect below, which runs after the
  // DOM has updated and the button is visible again.
  const closeAndRefocusTrigger = () => {
    shouldRefocusTriggerRef.current = true;
    setOpen(false);
  };

  useEffect(() => {
    if (open || !shouldRefocusTriggerRef.current) return;
    shouldRefocusTriggerRef.current = false;
    triggerRef.current?.focus();
  }, [open]);

  const resolvedLogoSrc = logoSrc ?? (variant === 'light' ? LIGHT_LOGO_SRC : DEFAULT_LOGO_SRC);
  const isLight = variant === 'light';
  const variantClass = isLight ? 'site-topnav--light' : '';
  const overlayClass = isLight ? '' : 'site-topnav--overlay';

  const items = useMemo(
    () => [
      { to: '/', label: 'HOME', end: true },
      { to: '/products', label: 'PRODUCTS' },
      {
        key: 'academy',
        label: 'ACADEMY',
        groups: academyMenu,
      },
      { to: '/contact-us', label: 'CONTACT US' },
      { to: '/about-us', label: 'ABOUT US' },
    ],
    []
  );

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) setMobileAccordionOpen(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = e => {
      if (e.key === 'Escape') closeAndRefocusTrigger();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  useBodyScrollLock(open);

  useEffect(() => {
    let raf = 0;
    const onScrollOrResize = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const threshold = window.innerHeight * 0.6;
        setShowSticky(window.scrollY > threshold);
      });
    };
    onScrollOrResize();
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, []);

  const stickyNav = (
    <NavShell
      className={[
        'site-topnav',
        'site-topnav--sticky',
        variantClass,
        showSticky ? 'is-visible' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      open={open && showSticky}
      setOpen={setOpen}
      items={items}
      logoSrc={resolvedLogoSrc}
      maskId={maskId}
      onTriggerClick={el => {
        triggerRef.current = el;
      }}
      onOpenPdf={setPdfViewer}
    />
  );

  const mobileMask = (
    <div
      id={maskId}
      className={[
        'site-topnav__mask',
        isLight ? 'site-topnav__mask--light' : '',
        open ? 'is-open' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-hidden={!open}
      onClick={e => {
        if (e.target === e.currentTarget) closeAndRefocusTrigger();
      }}
    >
      <div
        className="site-topnav__mask-inner"
        role="dialog"
        aria-modal="true"
        aria-label="Mobile menu"
      >
        <div className="site-topnav__mask-topbar">
          <div className="site-topnav__mask-icon" aria-hidden="true">
            <img
              className="site-topnav__mask-logo"
              alt=""
              referrerPolicy="no-referrer"
              src={resolvedLogoSrc}
            />
          </div>
          <button
            className="site-topnav__mask-close"
            type="button"
            aria-label="Close menu"
            onClick={closeAndRefocusTrigger}
          >
            <CloseIcon />
          </button>
        </div>

        <div className="site-topnav__mobile-panel" role="menu" aria-label="Mobile navigation">
          {items.map(item =>
            item.groups ? (
              <MobileMegaAccordion
                key={item.key}
                item={item}
                isOpen={mobileAccordionOpen}
                onToggleTop={() => setMobileAccordionOpen(v => !v)}
                onNavigate={() => setOpen(false)}
                onOpenPdf={setPdfViewer}
              />
            ) : (
              <NavLink
                key={item.to}
                role="menuitem"
                className={({ isActive }) =>
                  ['site-topnav__mobile-link', isActive ? 'is-active' : '']
                    .filter(Boolean)
                    .join(' ')
                }
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
              >
                {item.label}
              </NavLink>
            )
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <NavShell
        className={['site-topnav', overlayClass, variantClass, className].filter(Boolean).join(' ')}
        open={open}
        setOpen={setOpen}
        items={items}
        logoSrc={resolvedLogoSrc}
        maskId={maskId}
        onTriggerClick={el => {
          triggerRef.current = el;
        }}
        onOpenPdf={setPdfViewer}
      />
      {typeof document !== 'undefined' ? createPortal(stickyNav, document.body) : stickyNav}
      {typeof document !== 'undefined' ? createPortal(mobileMask, document.body) : mobileMask}
      <PdfViewerModal
        open={Boolean(pdfViewer)}
        url={pdfViewer?.url}
        title={pdfViewer?.title}
        onClose={() => setPdfViewer(null)}
      />
    </>
  );
};

export default SiteTopNav;
export { LIGHT_LOGO_SRC };
